'use client';

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, Scale } from 'lucide-react';
import { MOCK_WEIGHT_SAMPLES, FLOCK_GROWTH_SUMMARIES, BREED_TARGETS, COBB500_TARGETS } from './growthData';
import GrowthCurveChart from './GrowthCurveChart';

const STATUS_CONFIG = {
  on_track: { label: 'On Track', bg: 'bg-green-100', text: 'text-green-700', icon: Minus },
  above_target: { label: 'Above Target', bg: 'bg-blue-100', text: 'text-blue-700', icon: TrendingUp },
  below_target: { label: 'Below Target', bg: 'bg-amber-100', text: 'text-amber-700', icon: TrendingDown },
};

export default function FlockGrowthTable() {
  const [expandedFlock, setExpandedFlock] = useState<string | null>('flock-002');
  const [filterBirdType, setFilterBirdType] = useState('all');

  const summaries = useMemo(() => {
    if (filterBirdType === 'all') return FLOCK_GROWTH_SUMMARIES;
    return FLOCK_GROWTH_SUMMARIES.filter(f => f.birdType.toLowerCase() === filterBirdType);
  }, [filterBirdType]);

  function getFlockSamples(flockId: string) {
    return MOCK_WEIGHT_SAMPLES.filter(s => s.flockId === flockId)
      .sort((a, b) => b.ageDays - a.ageDays);
  }

  return (
    <div>
      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs font-medium text-gray-500">Bird Type:</span>
        {['all', 'broiler', 'layer', 'breeder'].map(t => (
          <button
            key={t}
            onClick={() => setFilterBirdType(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterBirdType === t
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {t === 'all' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {summaries.map(flock => {
          const cfg = STATUS_CONFIG[flock.status];
          const StatusIcon = cfg.icon;
          const isExpanded = expandedFlock === flock.flockId;
          const samples = getFlockSamples(flock.flockId);
          const breed = flock.breed;
          const targets = BREED_TARGETS[breed] ?? COBB500_TARGETS;

          return (
            <div key={flock.flockId} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Flock Header Row */}
              <button
                onClick={() => setExpandedFlock(isExpanded ? null : flock.flockId)}
                className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Scale size={16} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-900">{flock.flockNumber}</span>
                    <span className="text-xs text-gray-400">—</span>
                    <span className="text-sm text-gray-600 truncate">{flock.flockName}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                      <StatusIcon size={11} />
                      {cfg.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-xs text-gray-400">{flock.farm} · {flock.house}</span>
                    <span className="text-xs text-gray-400">Age: {flock.currentAgeDays}d</span>
                    <span className="text-xs text-gray-400">{flock.breed}</span>
                  </div>
                </div>
                {/* KPI chips */}
                <div className="hidden md:flex items-center gap-4 flex-shrink-0">
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Avg Weight</p>
                    <p className="text-sm font-bold text-gray-900">{flock.currentAvgWeightKg.toFixed(3)} kg</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Target</p>
                    <p className="text-sm font-bold text-gray-500">{flock.targetWeightKg.toFixed(3)} kg</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Variance</p>
                    <p className={`text-sm font-bold ${flock.weightVariancePct >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                      {flock.weightVariancePct >= 0 ? '+' : ''}{flock.weightVariancePct.toFixed(1)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Uniformity</p>
                    <p className={`text-sm font-bold ${flock.latestUniformityPct >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                      {flock.latestUniformityPct.toFixed(1)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">FCR</p>
                    <p className="text-sm font-bold text-gray-900">{flock.latestFcr.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">ADG</p>
                    <p className="text-sm font-bold text-gray-900">{flock.latestAdg.toFixed(1)}g</p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-2">
                  {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
              </button>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-5 py-5 space-y-5">
                  {/* Growth Curve Chart */}
                  <GrowthCurveChart
                    flockId={flock.flockId}
                    breed={flock.breed}
                    currentAgeDays={flock.currentAgeDays}
                  />

                  {/* Sampling Records Table */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Weight Sampling History</h4>
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            <th className="text-left px-3 py-2.5 font-semibold text-gray-500">Date</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Age (d)</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Sampled</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Avg Wt (kg)</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Target (kg)</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Variance</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Min / Max</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Uniformity</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">ADG (g)</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">Wkly Gain (g)</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-500">FCR</th>
                            <th className="text-left px-3 py-2.5 font-semibold text-gray-500">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {samples.map((s, idx) => {
                            const variance = ((s.avgWeightKg - s.targetWeightKg) / s.targetWeightKg) * 100;
                            const isLatest = idx === 0;
                            return (
                              <tr key={s.id} className={`hover:bg-gray-50 transition-colors ${isLatest ? 'bg-blue-50/40' : ''}`}>
                                <td className="px-3 py-2.5 text-gray-700 font-medium whitespace-nowrap">
                                  {s.recordDate}
                                  {isLatest && <span className="ml-1.5 text-blue-500 font-semibold text-xs">(latest)</span>}
                                </td>
                                <td className="px-3 py-2.5 text-right text-gray-700">{s.ageDays}</td>
                                <td className="px-3 py-2.5 text-right text-gray-700">{s.numberSampled}</td>
                                <td className="px-3 py-2.5 text-right font-semibold text-gray-900">{s.avgWeightKg.toFixed(3)}</td>
                                <td className="px-3 py-2.5 text-right text-gray-500">{s.targetWeightKg.toFixed(3)}</td>
                                <td className={`px-3 py-2.5 text-right font-semibold ${variance >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                  {variance >= 0 ? '+' : ''}{variance.toFixed(1)}%
                                </td>
                                <td className="px-3 py-2.5 text-right text-gray-500">
                                  {s.minWeightKg.toFixed(3)} / {s.maxWeightKg.toFixed(3)}
                                </td>
                                <td className={`px-3 py-2.5 text-right font-semibold ${s.uniformityPct >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                                  {s.uniformityPct.toFixed(1)}%
                                </td>
                                <td className="px-3 py-2.5 text-right text-gray-700">{s.avgDailyGainG.toFixed(1)}</td>
                                <td className="px-3 py-2.5 text-right text-gray-700">{s.weeklyGainG.toFixed(0)}</td>
                                <td className="px-3 py-2.5 text-right text-gray-700">{s.fcr.toFixed(2)}</td>
                                <td className="px-3 py-2.5 text-gray-400 max-w-[160px] truncate">{s.notes || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
